import { create } from 'zustand';
import type { Person, Relationship } from '../types';
import type { RelatieItem } from '../services/api/relationships.ts';
import type { PlekRol } from './useEditorStore.ts';
import type { PendingRelatie } from '../components/person-editor/PePlekInStamboom.tsx';

/**
 * #1825 deel 2 — het koppelvenster (spec scherm 2). `rol` = de rol van de gekózen persoon
 * t.o.v. de referentie (zelfde betekenis als `PlekRol`).
 */
export interface KoppelOpties {
  /** Referentiepersoon; leeg bij `voorOpslaan` als die nog geen id heeft. */
  persoonId: string;
  rol?: PlekRol;
  /**
   * `direct` = meteen vastleggen via /relationships/set; `voorOpslaan` = niets wegschrijven,
   * het item gaat via `onToevoegen` terug (de nieuwe persoon is daar het onderwerp).
   */
  modus: 'direct' | 'voorOpslaan';
  /** Concept van de referentie (naam, geboortedatum, geslacht) zolang die niet is opgeslagen. */
  concept?: Partial<Person>;
  /**
   * #1825 I5 — relaties van het concept die nog niet zijn opgeslagen (de nieuwe persoon is
   * onderwerp). Ze tellen mee voor elke regel (ouders, broer/zus, al gekoppeld).
   */
  pending?: RelatieItem[];
  onToevoegen?: (item: RelatieItem, weergave: PendingRelatie['weergave']) => void;
  /** Na een geslaagde directe koppeling, met de aangemaakte relaties (spec r.287-288, D2e). */
  onGekoppeld?: (relaties: Relationship[]) => void;
  /**
   * #1825 taak 4 — "Nieuwe persoon aanmaken" opent een nieuwe editor. Ligt het venster over een
   * open editor, dan geeft die hier zijn sluitvraag (niet-opgeslagen wijzigingen) en roept
   * `open` pas aan als sluiten mag.
   */
  nieuwePersoonVia?: (open: () => void) => void;
}

interface KoppelState {
  opties: KoppelOpties | null;
  /** Telt per `open`; AppModals remount het venster hierop (vers formulier). */
  sessie: number;
  open: (opties: KoppelOpties) => void;
  sluit: () => void;
}

export const useKoppelStore = create<KoppelState>((set) => ({
  opties: null,
  sessie: 0,
  open: (opties) => set((s) => ({ opties, sessie: s.sessie + 1 })),
  sluit: () => set({ opties: null }),
}));
